import { HttpClient } from "@angular/common/http";
import { DestroyRef, inject, Injector, isSignal } from "@angular/core";
import { extractDataFromApiResponse } from '@core/shared/request';
import { takeWhileAlive } from "@core/frontend-shared/utils";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { UiInputOptionsGroupedByirective } from "../input-options-grouped-by/input-options-grouped-by";
import { INPUT_OPTIONS_DYNAMIC_HOST } from "./supported_component";
import { AppRequestService, CrudServiceFactory } from '@core/frontend-shared';
import { isConstructor } from '@core/shared/utils';
import { applySimpleCrudQuery } from '@core/shared/crud';
import * as i0 from "@angular/core";
import * as i1 from "../input-options-grouped-by/input-options-grouped-by";
export let UiInputOptionsDynamicDirective = /*#__PURE__*/(() => {
  class UiInputOptionsDynamicDirective {
    constructor(injector, component, optionsGroupedBy) {
      this.injector = injector;
      this.component = component;
      this.optionsGroupedBy = optionsGroupedBy;
      this.destroyRef = inject(DestroyRef);
      this.mode = {
        type: 'item'
      };
      // optionValue and dataKey will be set on component too.
      // they are marked as inputs here too so that the directive can "spy" if there was any manual value set for these.
      this.optionValue = null;
      this.dataKey = null;
      this.loading = false;
    }
    ngOnInit() {
      if (this.input instanceof Observable) {
        this.useObservable(this.input);
      } else if (typeof this.input === 'string') {
        this.useApiPath(this.input);
      } else if (isConstructor(this.input)) {
        this.useAppRequest(this.input);
      } else if (typeof this.input === 'object' && this.input !== null) {
        if (typeof this.input.request !== 'undefined') {
          this.useAppRequest(this.input);
          return;
        } else if (typeof this.input.model !== 'undefined') {
          this.useCrudRequest(this.input);
          return;
        }
      }
    }
    useObservable(input) {
      // custom configuration is not supported by observable right now.
      // this.applyConfiguration();
      this.setComponentLoadingState(true);
      input.pipe(takeWhileAlive(this.destroyRef)).subscribe(value => {
        this.component.optionLabel = 'label';
        this.component.optionValue = 'value';
        // this.component.dataKey = 'value'
        this.applyOptions(value);
      });
    }
    useApiPath(input) {
      // console.warn('use raw http request (apiPath configuration) to fetch InputOptionsDynamic '+input)
      this.applyConfiguration();
      this.setComponentLoadingState(true);
      const http = this.injector.get(HttpClient);
      http.get(input).pipe(map(response => {
        return extractDataFromApiResponse(response, input);
      })).subscribe(val => {
        this.applyOptions(val);
      });
    }
    useAppRequest(input) {
      let Request;
      let data = {};
      if (typeof input === 'object') {
        // input is config object
        Request = input.request;
        // get payload to send
        // if data is set it can be a plain object or a "data factory" function.
        if (typeof input.data === 'function') data = input.data();else if (typeof input.data === 'object') data = input.data;
      } else {
        // input is Request Constructor
        Request = input;
      }
      this.applyConfiguration();
      this.setComponentLoadingState(true);
      const apiService = this.injector.get(AppRequestService);
      apiService.sendRequest(Request, data).subscribe(items => {
        if (!Array.isArray(items)) {
          throw new Error('InputOptionsDynamic [AppRequest mode] - received unexpected server response. Response must be an array of ItemModels!');
        }
        this.applyOptions(items);
      });
    }
    useCrudRequest(input) {
      this.applyConfiguration();
      this.setComponentLoadingState(true);
      const crud = this.injector.get(CrudServiceFactory).useDestroyRef(this.injector.get(DestroyRef)).createCrudService(input.model).useCrudEndpoint(input.useCrudEndpoint);
      crud.withCustomQuery(query => {
        applySimpleCrudQuery(query, input);
      }).readMany().subscribe(items => {
        this.applyOptions(items);
      });
    }
    setComponentLoadingState(isLoading) {
      if (isSignal(this.component.loading)) {
        this.component.loading.set(isLoading);
      } else {
        this.component.loading = isLoading;
        this.component.markForCheck();
      }
    }
    applyConfiguration() {
      // always set undefined optionLabel to "name" for item compatibility
      if (!this.component.optionLabel) this.component.optionLabel = 'name';
      // apply automatic configuration only if no custom configuration was specified.
      if (!this.optionValue && !this.dataKey) {
        if (this.mode.type === 'item') {
          this.component.dataKey = 'id'; // dataKey is used to compare object values by checking a certain property. 
        } else {
          this.component.optionValue = this.mode.prop || 'id'; // opposed to dataKey, this will set the component to use only the value of given prop name as field.
        }
        return;
      }
    }
    applyOptions(val) {
      if (!Array.isArray(val)) {
        throw new TypeError('Expected array input, got ' + typeof val);
      }
      if (this.optionsGroupedBy) {
        this.optionsGroupedBy.options = val;
      } else {
        if (isSignal(this.component.options)) {
          this.component.options.set(val);
        } else {
          this.component.options = val;
        }
      }
      this.setComponentLoadingState(false);
      this.component.markForCheck();
    }
    static {
      this.ɵfac = function UiInputOptionsDynamicDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UiInputOptionsDynamicDirective)(i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(INPUT_OPTIONS_DYNAMIC_HOST, 2), i0.ɵɵdirectiveInject(i1.UiInputOptionsGroupedByirective, 8));
      };
    }
    static {
      this.ɵdir = /*@__PURE__*/i0.ɵɵdefineDirective({
        type: UiInputOptionsDynamicDirective,
        selectors: [["", "InputOptionsDynamic", ""]],
        inputs: {
          input: [0, "InputOptionsDynamic", "input"],
          mode: [0, "InputOptionsDynamicMode", "mode"],
          optionValue: "optionValue",
          dataKey: "dataKey"
        }
      });
    }
  }
  return UiInputOptionsDynamicDirective;
})();